import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getConfig } from '../utils/config';
import { authenticatedFetch } from '../utils/authService';

const DeliveryOptions = ({ 
    invoice, 
    customer, 
    isExistingInvoice, 
    onDeliveryCompleted, 
    onError, 
    onGoBack 
}) => {
    // Debug props in detail
    console.log('DeliveryOptions received props:', { invoice, customer, isExistingInvoice });
    
    // More detailed debugging of invoice structure
    if (invoice) {
        console.log('Invoice structure details:', {
            hasUuid: !!invoice.uuid,
            hasFactura: !!invoice.factura,
            hasFacturaUuid: invoice.factura?.uuid,
            hasFacturaFolio: invoice.factura?.folio,
            hasFacturaSerie: invoice.factura?.serie,
            hasDownloadLinks: !!invoice.downloadLinks,
            hasDownload_links: !!invoice.download_links,
            downloadLinksPdf: invoice.downloadLinks?.pdf || 'none',
            download_linksPdf: invoice.download_links?.pdf || 'none',
            downloadLinksXml: invoice.downloadLinks?.xml || 'none',
            download_linksXml: invoice.download_links?.xml || 'none',
            hasCliente: !!invoice.cliente,
            clienteEmail: invoice.cliente?.email || 'none',
            clienteNombre: invoice.cliente?.nombre || 'none',
            clienteRfc: invoice.cliente?.rfc || 'none',
            fullInvoiceStructure: JSON.stringify(invoice, null, 2)
        });
    } else {
        console.log('No invoice data received');
    }
    
    if (customer) {
        console.log('Customer data received:', {
            email: customer.email,
            rfc: customer.rfc,
            razonSocial: customer.razonSocial,
            fullCustomer: JSON.stringify(customer, null, 2)
        });
    } else {
        console.log('No customer data received');
    }
    
    // Process the incoming invoice to ensure we have a unified structure
    // that works with both the new API response format and the old format
    const safeInvoice = (() => {
        // If no invoice data is provided, create fallback structure
        if (!invoice) {
            return {
                uuid: 'No disponible',
                total: 0,
                fechaTimbrado: new Date().toISOString(),
                downloadLinks: { pdf: '#', xml: '#' },
                factura: {
                    uuid: 'No disponible',
                    total: 0,
                    fecha_timbrado: new Date().toISOString(),
                    serie: '',
                    folio: ''
                },
                cliente: {
                    nombre: customer?.razonSocial || '',
                    rfc: customer?.rfc || '',
                    email: customer?.email || ''
                },
                download_links: { pdf: '#', xml: '#' }
            };
        }

        // Start with the original invoice data
        const processedInvoice = { ...invoice };

        // If invoice has the new structure directly (from ticket validation with existing invoice)
        if (invoice.factura && invoice.factura.uuid) {
            console.log('Processing new invoice format from existing invoice');
            // Map new structure to expected fields for backward compatibility
            processedInvoice.uuid = invoice.factura.uuid;
            processedInvoice.total = invoice.factura.total;
            processedInvoice.fechaTimbrado = invoice.factura.fecha_timbrado;
            
            // Ensure downloadLinks is properly mapped from download_links
            if (invoice.download_links) {
                processedInvoice.downloadLinks = invoice.download_links;
            }
        } else if (invoice.uuid) {
            // Using old structure - ensure we have both old and new formats
            processedInvoice.factura = {
                uuid: invoice.uuid,
                total: invoice.total,
                fecha_timbrado: invoice.fechaTimbrado,
                serie: invoice.serie || '',
                folio: invoice.folio || ''
            };
            
            // Ensure we have both old and new download link formats
            if (invoice.downloadLinks) {
                processedInvoice.download_links = invoice.downloadLinks;
            }
        }

        // Ensure both download link formats exist
        if (!processedInvoice.downloadLinks && processedInvoice.download_links) {
            processedInvoice.downloadLinks = processedInvoice.download_links;
        }
        if (!processedInvoice.download_links && processedInvoice.downloadLinks) {
            processedInvoice.download_links = processedInvoice.downloadLinks;
        }

        return processedInvoice;
    })();
    
    // Log which invoice we're using (real or fallback)
    console.log('Using invoice:', invoice ? 'REAL DATA' : 'FALLBACK DATA', safeInvoice);
    
    const [deliveryMethod, setDeliveryMethod] = useState('');
    // Use email from invoice if available, fallback to customer email
    // Check both old and new structures
    const clienteEmail = safeInvoice?.cliente?.email;
    const customerEmail = customer?.email;
    const invoiceEmail = safeInvoice?.email;
    
    console.log('Email sources:', { 
        clienteEmail, 
        customerEmail,
        invoiceEmail,
        customerProps: customer ? Object.keys(customer).join(', ') : 'none',
        invoiceProps: safeInvoice ? Object.keys(safeInvoice).join(', ') : 'none',
        clienteProps: safeInvoice?.cliente ? Object.keys(safeInvoice.cliente).join(', ') : 'none'
    });
    
    // Set a default email from any available source, prioritizing the new structure
    const [emailAddress, setEmailAddress] = useState(clienteEmail || customerEmail || invoiceEmail || '');
    const [isProcessing, setIsProcessing] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [deliveryData, setDeliveryData] = useState(null);
    const [downloadStatus, setDownloadStatus] = useState({
        pdf: { isDownloading: false, completed: false, error: null },
        xml: { isDownloading: false, completed: false, error: null }
    });

    const hasEmail = (clienteEmail || customerEmail || invoiceEmail) && (clienteEmail || customerEmail || invoiceEmail).trim() !== '';

    const handleDeliveryMethodChange = (method) => {
        setDeliveryMethod(method);
        
        if (method === 'email') {
            setShowEmailInput(true);
        } else {
            setShowEmailInput(false);
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
    
    const handleContinue = () => {
        if (deliveryData) {
            onDeliveryCompleted(deliveryData);
        }
    };

    const handleSendByEmail = async () => {
        const targetEmail = emailAddress.trim();
        
        if (!validateEmail(targetEmail)) {
            onError('Por favor ingrese una dirección de correo válida.');
            return;
        }

        setIsProcessing(true);

        try {
            // POST /facturas/email expects this exact structure
            const requestPayload = {
                factura: {
                    serie: safeInvoice.factura?.serie || '',
                    folio: safeInvoice.factura?.folio || 0,
                    uuid: safeInvoice.factura?.uuid || safeInvoice.uuid || '',
                    fecha_timbrado: safeInvoice.factura?.fecha_timbrado || safeInvoice.fechaTimbrado || '',
                    total: safeInvoice.factura?.total || safeInvoice.total || 0
                },
                cliente: {
                    nombre: safeInvoice.cliente?.nombre || customer?.razonSocial || '',
                    rfc: safeInvoice.cliente?.rfc || customer?.rfc || '',
                    email: targetEmail // Use the email entered by the user
                },
                download_links: {
                    xml: safeInvoice.download_links?.xml || safeInvoice.downloadLinks?.xml || '',
                    pdf: safeInvoice.download_links?.pdf || safeInvoice.downloadLinks?.pdf || ''
                }
            };
            
            console.log('Sending email with payload:', JSON.stringify(requestPayload, null, 2));
            
            // Call the API with the config
            const config = getConfig();
            const API_BASE_URL = config.apiBaseUrl;
            
            const response = await authenticatedFetch(`${API_BASE_URL}/facturas/email`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestPayload)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error al enviar el correo: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Email sent successfully:', JSON.stringify(data, null, 2));
            
            setEmailSent(true);
            
            // Store delivery data for when the user clicks the proceed button
            setDeliveryData({
                method: 'email',
                email: targetEmail,
                sentAt: new Date().toISOString()
            });
            
            // No more automatic timeout - user will click a button to proceed
            
        } catch (error) {
            console.error('Email sending error:', error);
            onError(error.message || 'Error al enviar el correo electrónico. Por favor intente con descarga directa.');
            setIsProcessing(false);
        }
    };

    const handleDirectDownload = () => {
        const deliveryData = {
            method: 'download',
            downloadedAt: new Date().toISOString()
        };
        
        onDeliveryCompleted(deliveryData);
    };
    
    // Función para forzar la descarga de archivos en lugar de abrirlos en el navegador
    const handleFileDownload = (url, fileName) => {
        console.log(`handleFileDownload called with:`, { url, fileName });
        
        if (!url || url === '#') {
            console.error('Invalid download URL:', url);
            onError('Enlace de descarga no disponible. Los enlaces pueden haber expirado o no estar disponibles.');
            return;
        }
        
        // Determinar si es PDF o XML para actualizar el estado correspondiente
        const fileType = fileName.toLowerCase().endsWith('.pdf') ? 'pdf' : 'xml';
        
        // Actualizar estado a descargando
        setDownloadStatus(prevStatus => ({
            ...prevStatus,
            [fileType]: {
                isDownloading: true,
                completed: false,
                error: null
            }
        }));
        
        console.log(`Iniciando descarga de ${fileName} desde ${url}`);
        
        // Verificar si la URL es válida
        try {
            // Test if the URL is valid
            const testUrl = new URL(url);
            console.log('URL is valid:', testUrl.href);
        } catch (e) {
            console.error('URL inválida:', url, e);
            setDownloadStatus(prevStatus => ({
                ...prevStatus,
                [fileType]: {
                    isDownloading: false,
                    completed: false,
                    error: 'URL de descarga inválida'
                }
            }));
            onError(`URL de descarga inválida: ${url}`);
            return;
        }
        
        // Método 1: Usando fetch para descargar y crear un blob
        fetch(url)
            .then(response => {
                // Verificar si la respuesta es exitosa
                if (!response.ok) {
                    throw new Error(`Error al descargar archivo: ${response.status} ${response.statusText}`);
                }
                return response.blob();
            })
            .then(blob => {
                // Verificar el tipo de contenido y tamaño
                console.log(`Contenido descargado: tipo=${blob.type}, tamaño=${blob.size} bytes`);
                
                if (blob.size < 100) { // Probablemente un error o archivo vacío
                    throw new Error('El archivo descargado está vacío o es demasiado pequeño');
                }
                
                // Crear un objeto URL del blob
                const blobUrl = window.URL.createObjectURL(blob);
                
                // Crear un elemento <a> temporal
                const downloadLink = document.createElement('a');
                downloadLink.href = blobUrl;
                downloadLink.download = fileName;
                
                // Agregar el enlace al DOM, simular clic y luego eliminar
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                
                // Liberar el objeto URL
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 100);
                
                console.log(`Descarga iniciada: ${fileName}`);
                
                // Actualizar estado a descarga completada
                setDownloadStatus(prevStatus => ({
                    ...prevStatus,
                    [fileType]: {
                        isDownloading: false,
                        completed: true,
                        error: null
                    }
                }));
                
                // Verificar si ambos archivos se descargaron para habilitar el botón de confirmación
                if (fileType === 'pdf' && downloadStatus.xml.completed) {
                    // Ambos descargados
                } else if (fileType === 'xml' && downloadStatus.pdf.completed) {
                    // Ambos descargados
                }
            })
            .catch(error => {
                console.error('Error en la descarga:', error);
                
                // Actualizar estado con el error
                setDownloadStatus(prevStatus => ({
                    ...prevStatus,
                    [fileType]: {
                        isDownloading: false,
                        completed: false,
                        error: error.message
                    }
                }));
                
                onError(`No se pudo descargar el archivo: ${error.message}`);
                
                // Método alternativo como fallback
                try {
                    console.log('Intentando método alternativo de descarga');
                    const fallbackLink = document.createElement('a');
                    fallbackLink.href = url;
                    fallbackLink.download = fileName;
                    fallbackLink.target = '_blank';
                    document.body.appendChild(fallbackLink);
                    fallbackLink.click();
                    document.body.removeChild(fallbackLink);
                    
                    // Si el fallback parece funcionar, actualizar estado
                    setTimeout(() => {
                        setDownloadStatus(prevStatus => ({
                            ...prevStatus,
                            [fileType]: {
                                isDownloading: false,
                                completed: true,
                                error: null
                            }
                        }));
                    }, 1000);
                } catch (fallbackError) {
                    console.error('Error en método alternativo:', fallbackError);
                    onError(`No se pudo descargar el archivo por ningún método. Por favor utilice la opción de envío por correo.`);
                }
            });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    };

    if (emailSent) {
        return (
            <div className="delivery-success">
                <div className="success-icon">📧</div>
                <h2>¡Correo enviado exitosamente!</h2>
                <p>Su factura ha sido enviada a:</p>
                <div className="sent-to-email">{emailAddress}</div>
                
                <div className="email-instructions">
                    <h3>Instrucciones importantes:</h3>
                    <ul>
                        <li>Revise su bandeja de entrada y carpeta de spam</li>
                        <li>El correo incluye los archivos XML y PDF</li>
                        <li>Guarde estos archivos en un lugar seguro</li>
                        <li>El XML es necesario para declaraciones fiscales</li>
                    </ul>
                </div>

                    <div className="backup-downloads">
                        <h4>Enlaces de descarga de respaldo:</h4>
                        <div className="download-links">
                            <button 
                                onClick={() => {
                                    const pdfUrl = safeInvoice.download_links?.pdf || safeInvoice.downloadLinks?.pdf;
                                    console.log('PDF URL:', pdfUrl);
                                    if (!pdfUrl || pdfUrl === '#') {
                                        onError('Enlace de descarga PDF no disponible. Los enlaces pueden haber expirado.');
                                        return;
                                    }
                                    handleFileDownload(pdfUrl, `factura-${safeInvoice.factura?.uuid || safeInvoice.uuid}.pdf`);
                                }}
                                className={`download-link pdf ${downloadStatus.pdf.isDownloading ? 'downloading' : ''} ${downloadStatus.pdf.completed ? 'completed' : ''} ${downloadStatus.pdf.error ? 'error' : ''}`}
                                disabled={downloadStatus.pdf.isDownloading}
                            >
                                {downloadStatus.pdf.completed ? '✅ PDF Descargado' : 
                                downloadStatus.pdf.error ? '❌ Error - Reintentar PDF' : 
                                downloadStatus.pdf.isDownloading ? '⏳ Descargando PDF...' : 
                                '📄 Descargar PDF'}
                            </button>
                            <button 
                                onClick={() => {
                                    const xmlUrl = safeInvoice.download_links?.xml || safeInvoice.downloadLinks?.xml;
                                    console.log('XML URL:', xmlUrl);
                                    if (!xmlUrl || xmlUrl === '#') {
                                        onError('Enlace de descarga XML no disponible. Los enlaces pueden haber expirado.');
                                        return;
                                    }
                                    handleFileDownload(xmlUrl, `factura-${safeInvoice.factura?.uuid || safeInvoice.uuid}.xml`);
                                }}
                                className={`download-link xml ${downloadStatus.xml.isDownloading ? 'downloading' : ''} ${downloadStatus.xml.completed ? 'completed' : ''} ${downloadStatus.xml.error ? 'error' : ''}`}
                                disabled={downloadStatus.xml.isDownloading}
                            >
                                {downloadStatus.xml.completed ? '✅ XML Descargado' : 
                                downloadStatus.xml.error ? '❌ Error - Reintentar XML' : 
                                downloadStatus.xml.isDownloading ? '⏳ Descargando XML...' : 
                                '📄 Descargar XML'}
                            </button>
                        </div>
                        <small>Los enlaces expiran en 24 horas</small>
                    </div>                {/* Botón para continuar a la siguiente pantalla */}
                <div className="form-actions continue-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleContinue}
                        className="btn-primary confirm-download-btn"
                    >
                        Continuar →
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="delivery-options">
            <div className="delivery-header">
                <h2>
                    {isExistingInvoice ? 'Opciones de Reenvío' : 'Entrega de Factura'}
                </h2>
                <p>
                    {isExistingInvoice 
                        ? 'Seleccione cómo desea recibir su factura existente'
                        : 'Seleccione cómo desea recibir su factura recién generada'
                    }
                </p>
            </div>

            {/* Invoice Summary */}
            <div className="invoice-summary-card">
                <h3>Resumen de la Factura</h3>
                <div className="summary-details">
                    <div className="detail-row">
                        <span className="label">UUID:</span>
                        <span className="value uuid">{safeInvoice.factura?.uuid || safeInvoice.uuid}</span>
                    </div>
                    {safeInvoice.factura && safeInvoice.factura.serie && safeInvoice.factura.folio && (
                        <div className="detail-row">
                            <span className="label">Serie-Folio:</span>
                            <span className="value">{safeInvoice.factura.serie}-{safeInvoice.factura.folio}</span>
                        </div>
                    )}
                    <div className="detail-row">
                        <span className="label">Total:</span>
                        <span className="value total">{formatCurrency(safeInvoice.factura?.total || safeInvoice.total)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Fecha:</span>
                        <span className="value">
                            {new Date(safeInvoice.factura?.fecha_timbrado || safeInvoice.fechaTimbrado).toLocaleDateString('es-MX')}
                        </span>
                    </div>
                    {safeInvoice.cliente && safeInvoice.cliente.nombre && (
                        <div className="detail-row">
                            <span className="label">Cliente:</span>
                            <span className="value">{safeInvoice.cliente.nombre}</span>
                        </div>
                    )}
                    {safeInvoice.cliente && safeInvoice.cliente.rfc && (
                        <div className="detail-row">
                            <span className="label">RFC:</span>
                            <span className="value">{safeInvoice.cliente.rfc}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Delivery Method Selection */}
            <div className="delivery-methods">
                <h3>Método de Entrega</h3>
                
                {/* Email Option */}
                <div className={`delivery-option ${deliveryMethod === 'email' ? 'selected' : ''}`}>
                    <input
                        type="radio"
                        id="email-delivery"
                        name="deliveryMethod"
                        value="email"
                        checked={deliveryMethod === 'email'}
                        onChange={() => handleDeliveryMethodChange('email')}
                    />
                    <label htmlFor="email-delivery" className="delivery-option-label">
                        <div className="option-icon">📧</div>
                        <div className="option-content">
                            <h4>Envío por Correo Electrónico</h4>
                            <p>Reciba XML y PDF directamente en su email</p>
                            {hasEmail && (
                                <small className="current-email">
                                    Email registrado: {clienteEmail || customerEmail || invoiceEmail}
                                </small>
                            )}
                        </div>
                    </label>
                </div>

                {/* Email Input (shown when email delivery method is selected) */}
                {showEmailInput && (
                    <div className="email-input-section">
                        <div className="input-group">
                            <label htmlFor="emailAddress">Correo Electrónico para envío:</label>
                            <input
                                id="emailAddress"
                                type="email"
                                value={emailAddress}
                                onChange={(e) => setEmailAddress(e.target.value)}
                                placeholder="ejemplo@correo.com"
                                disabled={isProcessing}
                            />
                            {hasEmail && (
                                <small className="edit-email-help">
                                    Puede editar el correo electrónico si desea enviarlo a una dirección diferente.
                                </small>
                            )}
                        </div>
                    </div>
                )}

                {/* Download Option */}
                <div className={`delivery-option ${deliveryMethod === 'download' ? 'selected' : ''}`}>
                    <input
                        type="radio"
                        id="download-delivery"
                        name="deliveryMethod"
                        value="download"
                        checked={deliveryMethod === 'download'}
                        onChange={() => handleDeliveryMethodChange('download')}
                    />
                    <label htmlFor="download-delivery" className="delivery-option-label">
                        <div className="option-icon">💾</div>
                        <div className="option-content">
                            <h4>Descarga Directa</h4>
                            <p>Descargue inmediatamente XML y PDF</p>
                            <small>Enlaces válidos por 24 horas</small>
                        </div>
                    </label>
                </div>
            </div>

            {/* Email Delivery Actions */}
            {deliveryMethod === 'email' && (
                <div className="email-delivery-section">
                    <div className="email-info">
                        <h4>Información del Envío</h4>
                        <ul>
                            <li>Se enviarán ambos archivos (XML y PDF) adjuntos</li>
                            <li>El correo incluirá instrucciones de uso</li>
                            <li>Revise su carpeta de spam si no recibe el correo</li>
                        </ul>
                    </div>

                    <button
                        onClick={handleSendByEmail}
                        disabled={!emailAddress.trim() || isProcessing}
                        className="btn-primary send-email-btn"
                    >
                        {isProcessing ? 'Enviando...' : '📧 Enviar por Correo'}
                    </button>
                </div>
            )}

            {/* Download Delivery Actions */}
            {deliveryMethod === 'download' && (
                <div className="download-delivery-section">
                    <div className="download-info">
                        <h4>Descarga de Archivos</h4>
                        <p>Haga clic en los siguientes enlaces para descargar sus archivos:</p>
                    </div>

                    <div className="download-links">
                        <button 
                            onClick={() => {
                                const pdfUrl = safeInvoice.download_links?.pdf || safeInvoice.downloadLinks?.pdf;
                                console.log('Download section - PDF URL:', pdfUrl);
                                if (!pdfUrl || pdfUrl === '#') {
                                    onError('Enlace de descarga PDF no disponible. Los enlaces pueden haber expirado.');
                                    return;
                                }
                                handleFileDownload(pdfUrl, `factura-${safeInvoice.factura?.uuid || safeInvoice.uuid}.pdf`);
                            }}
                            className={`download-link pdf ${downloadStatus.pdf.isDownloading ? 'downloading' : ''} ${downloadStatus.pdf.completed ? 'completed' : ''} ${downloadStatus.pdf.error ? 'error' : ''}`}
                            disabled={downloadStatus.pdf.isDownloading}
                        >
                            <div className="link-icon">
                                {downloadStatus.pdf.completed ? '✅' : 
                                downloadStatus.pdf.error ? '❌' : 
                                downloadStatus.pdf.isDownloading ? '⏳' : '📄'}
                            </div>
                            <div className="link-content">
                                <strong>
                                    {downloadStatus.pdf.isDownloading ? 'Descargando PDF...' : 
                                    downloadStatus.pdf.completed ? 'PDF Descargado' : 
                                    downloadStatus.pdf.error ? 'Error al descargar PDF' :
                                    'Descargar PDF'}
                                </strong>
                                <small>
                                    {downloadStatus.pdf.error ? 'Haga clic para intentar de nuevo' : 
                                    'Archivo para impresión y visualización'}
                                </small>
                            </div>
                        </button>

                        <button
                            onClick={() => {
                                const xmlUrl = safeInvoice.download_links?.xml || safeInvoice.downloadLinks?.xml;
                                console.log('Download section - XML URL:', xmlUrl);
                                if (!xmlUrl || xmlUrl === '#') {
                                    onError('Enlace de descarga XML no disponible. Los enlaces pueden haber expirado.');
                                    return;
                                }
                                handleFileDownload(xmlUrl, `factura-${safeInvoice.factura?.uuid || safeInvoice.uuid}.xml`);
                            }}
                            className={`download-link xml ${downloadStatus.xml.isDownloading ? 'downloading' : ''} ${downloadStatus.xml.completed ? 'completed' : ''} ${downloadStatus.xml.error ? 'error' : ''}`}
                            disabled={downloadStatus.xml.isDownloading}
                        >
                            <div className="link-icon">
                                {downloadStatus.xml.completed ? '✅' : 
                                downloadStatus.xml.error ? '❌' : 
                                downloadStatus.xml.isDownloading ? '⏳' : '📄'}
                            </div>
                            <div className="link-content">
                                <strong>
                                    {downloadStatus.xml.isDownloading ? 'Descargando XML...' : 
                                    downloadStatus.xml.completed ? 'XML Descargado' : 
                                    downloadStatus.xml.error ? 'Error al descargar XML' :
                                    'Descargar XML'}
                                </strong>
                                <small>
                                    {downloadStatus.xml.error ? 'Haga clic para intentar de nuevo' : 
                                    'Archivo fiscal requerido para declaraciones'}
                                </small>
                            </div>
                        </button>
                    </div>

                    <div className="download-instructions">
                        <h5>⚠️ Importante:</h5>
                        <ul>
                            <li>Si requiere cancelar la factura, mande un mensaje por WhatsApp al (55) 4620 0804.</li>
                            <li>Guarde ambos archivos en un lugar seguro</li>
                            <li>El XML es obligatorio para sus declaraciones fiscales</li>
                            <li>Los enlaces expiran en 24 horas</li>
                        </ul>
                    </div>

                    <button
                        onClick={handleDirectDownload}
                        className="btn-primary confirm-download-btn"
                        disabled={!downloadStatus.pdf.completed || !downloadStatus.xml.completed}
                    >
                        ✅ Confirmar Descarga Completada
                    </button>
                    
                    {(!downloadStatus.pdf.completed || !downloadStatus.xml.completed) && (
                        <div className="download-status-message">
                            {!downloadStatus.pdf.completed && !downloadStatus.xml.completed
                                ? 'Descargue ambos archivos antes de continuar'
                                : !downloadStatus.pdf.completed
                                ? 'Falta descargar el PDF antes de continuar'
                                : 'Falta descargar el XML antes de continuar'
                            }
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="form-actions">
                {onGoBack && (
                    <button
                        onClick={onGoBack}
                        className="btn-secondary"
                        disabled={isProcessing}
                    >
                        ← Regresar
                    </button>
                )}
                
                {!deliveryMethod && (
                    <div className="selection-hint">
                        Seleccione un método de entrega para continuar
                    </div>
                )}
            </div>
        </div>
    );
};

DeliveryOptions.propTypes = {
    invoice: PropTypes.shape({
        // Support both old structure
        uuid: PropTypes.string,
        total: PropTypes.number,
        fechaTimbrado: PropTypes.string,
        email: PropTypes.string,
        xmlPath: PropTypes.string,
        pdfPath: PropTypes.string,
        downloadLinks: PropTypes.shape({
            pdf: PropTypes.string,
            xml: PropTypes.string
        }),
        // New structure fields
        factura: PropTypes.shape({
            serie: PropTypes.string,
            folio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            uuid: PropTypes.string,
            fecha_timbrado: PropTypes.string,
            total: PropTypes.number
        }),
        cliente: PropTypes.shape({
            nombre: PropTypes.string,
            rfc: PropTypes.string,
            email: PropTypes.string
        }),
        download_links: PropTypes.shape({
            pdf: PropTypes.string,
            xml: PropTypes.string
        })
    }),
    customer: PropTypes.shape({
        email: PropTypes.string,
        rfc: PropTypes.string.isRequired,
        razonSocial: PropTypes.string.isRequired
    }),
    isExistingInvoice: PropTypes.bool,
    onDeliveryCompleted: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onGoBack: PropTypes.func
};

DeliveryOptions.defaultProps = {
    isExistingInvoice: false,
    onGoBack: null
};

export default DeliveryOptions;